import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  DollarSign, 
  XCircle, 
  Calendar,
  Eye,
  ArrowUpRight,
  ArrowDownLeft
} from 'lucide-react';
import { SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import DatePicker from '../../components/client/DatePicker';
import { useAdminTransactionsData } from '../../hooks/useAdminTransactions';
import { formatDate, formatTime } from '../../utils/dateUtils';

const AdminTransactions = () => {
  const navigate = useNavigate();
  
  // Use the custom hook for all transaction logic
  const {
    transactions,
    isLoading,
    error,
    filters,
    typeOptions,
    statusOptions,
    handleFilterChange,
    getStatusConfig,
    getTypeIcon,
    handleSearch,
    statistics
  } = useAdminTransactionsData();

  // Helper function to truncate text
  const truncateText = (text, maxLength = 12) => {
    if (!text) return 'N/A';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Extract transactions from the hook response
  // The hook already handles the data extraction, so transactions should be an array
  const allTransactions = Array.isArray(transactions) ? transactions : (transactions?.data || []);
  // Filter out pending transactions - don't show them
  const transactionList = allTransactions.filter(transaction => transaction.status !== 'pending');

  // Define table columns
  const columns = [
    {
      key: 'transaction_id',
      label: 'Transaction ID / Booking',
      sortable: true,
      width: 'w-32',
      // render: (transaction) => (
      //   <span className="font-mono text-sm font-medium text-primary-600">
      //     #{transaction.transaction_id || transaction.payment_reference || transaction.id}
      //   </span>
      // )
      render: (transaction) => {
        const transactionRef = transaction.transaction_id || transaction.payment_reference || transaction.id || '';
        const bookingRef = transaction.booking?.booking_reference || transaction.booking_id || 'N/A';
        
        return (
          <div className="min-w-0 space-y-1">
            <span className="font-mono text-xs font-medium text-primary-600 block truncate" title={`#${transactionRef}`}>
              #{truncateText(transactionRef, 12)}
            </span>
            {transaction.booking_id ? (
              <button
                onClick={() => navigate(`/admin/bookings/${transaction.booking_id}`)}
                className="font-mono text-xs font-medium text-primary-600 hover:underline block truncate"
                title={`#${bookingRef}`}
              >
                #{truncateText(bookingRef, 12)}
              </button>
            ) : (
              <span className="font-mono text-xs text-gray-400 block">N/A</span>
            )}
          </div>
        );
      }
    },
    
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      width: 'w-24',
      render: (transaction) => {
        const type = transaction.type || transaction.payment_type || 'payment';
        return (
          <div className="flex items-center gap-2">
            {getTypeIcon(type)}
            <span className="text-sm font-medium text-gray-900 capitalize">
              {type.replace('_', ' ')}
            </span>
          </div>
        );
      }
    },
    // {
    //   key: 'booking',
    //   label: 'Booking',
    //   sortable: true,
    //   width: 'w-32',
    //   render: (transaction) => (
    //     <button
    //       onClick={() => transaction.booking_id && navigate(`/admin/bookings/${transaction.booking_id}`)}
    //       className="font-mono text-sm font-medium text-primary-600 hover:underline"
    //     >
    //       #{transaction.booking?.booking_reference || transaction.booking_id || 'N/A'}
    //     </button>
    //   )
    // },
    {
      key: 'client',
      label: 'Client',
      sortable: true,
      width: 'w-48',
      render: (transaction) => (
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {transaction.booking?.client?.name || transaction.client?.name || 'Unknown Client'}
          </p>
        </div>
      )
    },
    {
      key: 'amount',
      label: 'Amount',
      sortable: true,
      width: 'w-24',
      render: (transaction) => {
        const formatCurrency = (value) => {
          const num = parseFloat(value);
          if (isNaN(num) || num === null || num === undefined) {
            return '0.00';
          }
          return num.toFixed(2);
        };
        const amount = parseFloat(transaction.amount || 0);
        const isNegative = transaction.type === 'refund' || transaction.type === 'payout';
        return (
          <div className={`text-right ${isNegative ? 'text-red-600' : 'text-gray-900'}`}>
            <p className="text-sm font-medium">
              {isNegative ? '-' : '+'}${formatCurrency(Math.abs(amount))}
            </p>
          </div>
        );
      }
    },
    {
      key: 'created_at',
      label: 'Date',
      sortable: true,
      width: 'w-32',
      render: (transaction) => (
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900">
              {formatDate(transaction.created_at, 'medium')}
            </div>
            {transaction.created_at && (
              <div className="text-xs text-gray-500">
                {formatTime(transaction.created_at)}
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: 'w-28',
      render: (transaction) => {
        const statusConfig = getStatusConfig(transaction.status);
        const StatusIcon = statusConfig.icon;
        return (
          <span className="whitespace-nowrap flex-shrink-0">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {statusConfig.label}
            </span>
          </span>
        );
      }
    }
  ];

  // Define table actions
  const actions = [
    {
      label: 'View Details',
      icon: Eye,
      variant: 'default',
      onClick: (transaction) => {
        if (transaction.booking_id) {
          navigate(`/admin/bookings/${transaction.booking_id}`);
        }
      }
    }
  ];

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
        <div className="bg-white border rounded-lg p-6">
          <div className="text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Transactions</h3>
            <p className="text-gray-600">
              {error?.response?.data?.message || 'Failed to load transactions. Please try again later.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-4">
        {/* Header */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <DollarSign className="w-6 h-6" />
                Transactions
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                View all financial transactions across the platform
              </p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Inflow</p>
                <p className="text-2xl font-bold text-green-600 mt-1">
                  ${statistics.totalInflow.toFixed(2)}
                </p>
              </div>
              <ArrowUpRight className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Outflow</p>
                <p className="text-2xl font-bold text-red-600 mt-1">
                  ${statistics.totalOutflow.toFixed(2)}
                </p>
              </div>
              <ArrowDownLeft className="w-8 h-8 text-red-500" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Net Amount</p>
                <p className={`text-2xl font-bold mt-1 ${statistics.netAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  ${Math.abs(statistics.netAmount).toFixed(2)}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <SearchSelect
              label="Type"
              value={filters.type}
              onChange={(value) => handleFilterChange('type', value)}
              options={typeOptions}
            />
            <SearchSelect
              label="Status"
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
              options={statusOptions}
            />
            <DatePicker
              label="From Date"
              value={filters.date_from}
              onChange={(value) => handleFilterChange('date_from', value)}
            />
            <DatePicker
              label="To Date"
              value={filters.date_to}
              onChange={(value) => handleFilterChange('date_to', value)}
            />
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <DataTable
            data={transactionList}
            columns={columns}
            actions={actions}
            isLoading={isLoading}
            searchPlaceholder="Search transactions by ID, booking, or client..."
            onSearch={handleSearch}
            emptyMessage="No transactions found"
          />
        </div>
      </div>
    </div>
  );
};

export default AdminTransactions;

